import { postRequest, getRequest, putRequest, patchRequest, deleteRequest, apiDownload } from "@/services/backendApi";
import type { Payload, ErrorHandler } from "@/services/backendApi";
import { useAuthStore } from "@/stores/authStore";
import { useLicenseQuotaStore } from "@/stores/licenseQuotaStore";
import router from "@/router";

/**
 * Auf öffentlichen Routen (requiresAuth: false, z. B. /vorort, /einloggen)
 * existiert keine Kanzlei-Session. Ein 401 dort darf NICHT den Logout-Flow
 * auslösen: der würde den Besucher hart zur Login-Seite umleiten und über
 * den Auth-Broadcast sogar angemeldete Tabs desselben Geräts abmelden
 * (Vor-Ort-Szenario: Mitarbeiter reicht sein Gerät an den Kunden weiter).
 */
function isPublicRoute(): boolean {
  return router.currentRoute.value?.meta?.requiresAuth === false;
}

// POST
export function postRequestGeneric(endpoint: string, payload: Payload, includeCredentials: boolean): Promise<any> {
  return postRequest(endpoint, payload, handleApiErrorGeneric, includeCredentials);
}

// GET
export function getRequestGeneric(endpoint: string): Promise<any> {
  return getRequest(endpoint, handleApiErrorGeneric, true);
}

// PUT
export function putRequestGeneric(endpoint: string, payload: Payload, includeCredentials: boolean): Promise<any> {
  return putRequest(endpoint, payload, handleApiErrorGeneric, includeCredentials);
}

// PATCH
export function patchRequestGeneric(endpoint: string, payload: Payload, includeCredentials: boolean = true): Promise<any> {
  return patchRequest(endpoint, payload, handleApiErrorGeneric, includeCredentials);
}

// DELETE
export function deleteRequestGeneric(endpoint: string, payload: Payload = null): Promise<any> {
  return deleteRequest(endpoint, payload, handleApiErrorGeneric, true);
}

// DOWNLOAD
export function downloadGeneric(endpoint: string, filename: string): Promise<void> {
  return apiDownload(endpoint, filename, handleApiErrorGeneric);
}

// error handling

export const handleApiErrorGeneric: ErrorHandler = async (response) => {
  const data = await response.json().catch(() => ({}));
  const errorCode = data.error_code || response.status;

  // License quota violations (issue #55) surface as HTTP 402 with a
  // structured body ({code, message, plan, licensed*, used*}). Pop the
  // global upgrade dialog so every write path that hits a quota shows
  // the same actionable UI without per-call-site wiring.
  if (
    response.status === 402 &&
    typeof data.code === "string" &&
    (data.code === "license_user_quota_exceeded" ||
      data.code === "license_location_quota_exceeded")
  ) {
    useLicenseQuotaStore().showQuota(data);
    throw {
      message: data.message || "Lizenzlimit erreicht.",
      errorCode: 402,
      quota: data,
    };
  }

  // KI-Seiten Kontingent erschöpft (HTTP 402 + code "ai_page_quota_exceeded").
  // Body enthält locationName, pagesAvailable, requestedPages.
  if (
    response.status === 402 &&
    typeof data.code === "string" &&
    data.code === "ai_page_quota_exceeded"
  ) {
    throw {
      message:
        data.message ||
        "Das KI-Seiten-Kontingent für diesen Standort ist aufgebraucht.",
      errorCode: 402,
      aiPageQuota: data,
    };
  }

  let message: string;

  switch (errorCode) {
    case 400:
    case 405:
    case 500:
      message = "Es ist ein interner Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
      break;

    case 401:
      if (!isPublicRoute()) useAuthStore().logout();
      message = "Ihre Sitzung ist abgelaufen, bitte melden Sie sich erneut an.";
      break;

    case 402:
      message = "Ungültige E-Mail-Adresse oder Passwort.";
      break;

    case 403:
      message = "Anfrage fehlgeschlagen: Sie haben keine Berechtigung, diese Aktion auszuführen.";
      break;

    case 404:
      message = "Anfrage fehlgeschlagen: Die angeforderte Ressource existiert nicht.";
      break;

    case 406: // special case used in handler
      message = "";
      break;

    case 408:
      message = "Der Link zum Zurücksetzen des Passworts ist abgelaufen. Bitte fordern Sie einen neuen Link an.";
      break;

    case 409:
      // 409 kann aus vielen Kontexten kommen (E-Mail vergeben, Namens-
      // konflikt, ...). Bevorzugt die deutsche Server-Meldung anzeigen,
      // sonst neutraler Konflikt-Text statt der irreführenden
      // Pauschalmeldung "E-Mail bereits registriert".
      message =
        typeof data.message === "string" && data.message.trim()
          ? data.message
          : "Konflikt: Die Aktion konnte nicht ausgeführt werden.";
      break;

    case 410:
      // Gelöschte Akte (fall.respondIfCaseDeleted): Die Servermeldung nennt
      // den Grund samt Löschdatum.
      message =
        typeof data.message === "string" && data.message.trim()
          ? data.message
          : "Die angeforderte Akte wurde gelöscht und ist nicht mehr verfügbar.";
      break;

    case 429:
      // Kundenfeedback 18.09.2026 (unerklärliche Abmeldungen): Ein
      // Anfragelimit ist kein Sitzungsproblem. Die Sitzung bleibt bestehen,
      // der Nutzer sieht nur den Hinweis und kann es gleich erneut versuchen.
      message = "Anfragelimit erreicht. Bitte versuchen Sie es später erneut.";
      break;

    default:
      message = "Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.";
  }

  // `code` ist der maschinenlesbare Fehlercode des Backends (z. B.
  // "user_already_in_organization"). Aufrufer koennen damit einzelne
  // Konfliktarten unterscheiden, ohne Meldungstexte zu vergleichen.
  // `conflict` beschreibt bei 409 den blockierenden Datensatz (wer, warum
  // unsichtbar, uebernehmbar?) — Bugreport 14.09.26 Benutzerverwaltung.
  // `mergedIntoCaseId`: 410 einer zusammengeführten Akte nennt die Zielakte
  // (fall.respondIfCaseDeleted), wenn der Nutzer sie sehen darf.
  throw { message, errorCode: errorCode, code: data.code, conflict: data.conflict, originalMessage: data.message, mergedIntoCaseId: data.mergedIntoCaseId };
};
